import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Modal from '../../molecules/Modal';
import Button from '../../atoms/Button';
import Drawer from '../../atoms/Drawer';
import Textbox from '../../atoms/Textbox';
import IconButton from '../../atoms/IconButton';
import { Icon } from '@material-ui/core';
import { AttachedFile } from '../../../interfaces/AttachedFile';
import { addBase64Header } from '../../../utils/StringUtil';
import { validRequired, checkSize } from '../../../utils/ValidationCheck';
import { getMessageFromMasterInfo } from '../../../redux/reducers/MasterInfoReducer/utils/MasterInfoStateUtils';
import MESSAGE_MASTER_KEYS from '../../../redux/reducers/MasterInfoReducer/definitions/MessageMasterKeys';
import { AppState } from '../../../redux/modules/store';
import ImageFileEdit from '../ImageFileEdit';

export interface ImageViewerDrawerProps {
  isOpen: boolean;
  index: number;
  attachedFileList: AttachedFile[];
  onClose: () => void;
  changeFileName?: (i: number, name: string) => void;
  removeFile?: (i: number) => void;
  isReadOnly?: boolean;
  defaultViewportMetaContent: string;
  userScalableViewportMetaContent: string;
  changeFileData?: (i: number, name: string) => void;
  onModal?: boolean;
}

const ImageViewerDrawer: React.FC<ImageViewerDrawerProps> = ({
  isOpen,
  index,
  attachedFileList,
  onClose,
  changeFileName,
  removeFile,
  isReadOnly = false,
  defaultViewportMetaContent,
  userScalableViewportMetaContent,
  changeFileData,
  onModal = false,
}) => {
  const fileNameMaxSize = 150;
  const [isOpenEditModal, setOpenEditModal] = useState(false);
  const [isOpenDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileNameInvalidMsg, setFileNameInvalidMsg] = useState('');

  const masterInfo = useSelector((state: AppState) => state.masterInfo);

  const [isEditMode, setIsEditMode] = useState(false);

  const closeThis = () => {
    setOpenEditModal(false);
    setOpenDeleteConfirmModal(false);
    setIsEditMode(false);
    onClose();
  };

  const onSaveFileName = () => {
    if (!changeFileName) return;

    const requiredMessage = validRequired(fileName);
    if (requiredMessage) {
      setFileNameInvalidMsg(requiredMessage);
      return;
    }

    const isValidSize = checkSize(fileName, fileNameMaxSize);
    if (!isValidSize) {
      setFileNameInvalidMsg(getMessageFromMasterInfo(masterInfo, MESSAGE_MASTER_KEYS.E_S003, fileNameMaxSize.toString()));
      return;
    }

    setFileNameInvalidMsg('');
    changeFileName(index, fileName);
    setOpenEditModal(false);
  };

  const onRemove = () => {
    removeFile && removeFile(index);
    closeThis();
  };

  /**
   * メタデータ(viewport)変更
   * イメージビュアーのコンポーネントのみ、iPadからピンチイン・アウトができるようにメタデータを更新する。
   */
  const changeMetadataViewport = () => {
    const headData = document.head.children;
    for (let i = 0; i < headData.length; i++) {
      const nameVal = headData[i].getAttribute('name');
      if (nameVal) {
        if (nameVal.indexOf('viewport') !== -1) {
          const currentViewportMetaContent = headData[i].getAttribute('content');
          const toBeViewportContent = isOpen ? userScalableViewportMetaContent : defaultViewportMetaContent;

          // ビュアードロワーが表示の場合のみピンチインアウトができるようにメタデータ変更。
          // ビュアードロワーが非表示になったら、ピンチインアウトができないようにメタデータを戻す。
          if (currentViewportMetaContent !== toBeViewportContent) {
            headData[i].removeAttribute('content');
            headData[i].setAttribute('content', toBeViewportContent);
          }
        }
      }
    }
  };
  changeMetadataViewport();

  const handleClickEdit = () => {
    setIsEditMode((prev) => !prev);
  };

  return (
    <div className='camera-list__item-image'>
      <Drawer onClose={closeThis} position='bottom' isOpen={isOpen}>
        {!isEditMode ? (
          <div className={onModal ? 'camera-edit-on-modal' : 'camera-edit'}>
            <div className='camera-edit__header'>
              <div className='camera-edit__header-left'>
                <IconButton icon='close' onClick={closeThis} data-testid='btn-close-viewer' />
              </div>
              <div className='camera-edit__header-center'>
                <p className='camera-edit__header-name' data-testid='filename'>
                  {attachedFileList.length > index ? attachedFileList[index].fileName : ''}
                </p>
                {!isReadOnly && (
                  <Button
                    leftIcon='edit'
                    type='transparent'
                    onClick={() => {
                      setFileName(attachedFileList.length > index ? attachedFileList[index].fileName : '');
                      setFileNameInvalidMsg('');
                      setOpenEditModal(true);
                    }}
                    data-testid='btn_edit'
                  />
                )}
              </div>
              {!isReadOnly && (
                <div className='camera-edit__header-right'>
                  <Button label='画像を編集' type='outlined' leftIcon='draw' onClick={handleClickEdit} data-testid='btn_edit_confirm' />
                </div>
              )}
              {!isReadOnly && (
                <div className='camera-edit__header-right'>
                  <Button
                    label='画像を削除'
                    leftIcon='deleteForever'
                    type='outlined'
                    onClick={() => setOpenDeleteConfirmModal(true)}
                    data-testid='btn_delete_confirm'
                    className='u-ml8'
                    isDanger
                  />
                </div>
              )}
            </div>

            <div className='camera-edit__content camera-left-drawer__content'>
              <img
                className='camera-edit__content-image'
                src={attachedFileList.length > index ? addBase64Header(attachedFileList[index].dataObject) : ''}
                alt=''
                style={{ transform: 'translate(0, 0), scale(1.0)' }}
              />
            </div>
          </div>
        ) : (
          <ImageFileEdit
            base64Image={attachedFileList.length > index ? addBase64Header(attachedFileList[index].dataObject) : ''}
            index={index}
            closeViewer={closeThis}
            changeFileData={changeFileData}
          />
        )}
      </Drawer>

      {/* ファイル名変更 モーダル */}
      <Modal
        open={isOpenEditModal}
        onRendered={() => undefined}
        onBackdropClick={() => setOpenEditModal(false)}
        onEscapeKeyDown={() => setOpenEditModal(false)}
        surfaceClassName='u-mv-auto'>
        <Modal.Header hasCloseButton={false}>ファイル名の変更</Modal.Header>
        <Modal.Content className='u-pb24'>
          <Textbox
            value={fileName}
            onChange={(e) => {
              setFileName(e.target.value);
            }}
            width='full'
            data-testid='filename_input'
          />
          <p className='u-text-danger'>{fileNameInvalidMsg}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button type='secondary' label='キャンセル' onClick={() => setOpenEditModal(false)} />
          <Button label='変更' onClick={onSaveFileName} data-testid='btn_save' />
        </Modal.Actions>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal
        open={isOpenDeleteConfirmModal}
        onBackdropClick={() => setOpenDeleteConfirmModal(false)}
        onEscapeKeyDown={() => setOpenDeleteConfirmModal(false)}
        surfaceClassName='u-mv-auto'>
        <Modal.Header hasCloseButton={false}>画像を削除しますか？</Modal.Header>
        <Modal.Content className='u-pb24'>
          <p className='u-text-danger'>
            <Icon className='u-text-middle u-mr8'>error</Icon>
            削除した画像は元に戻せません。
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button type='secondary' label='キャンセル' onClick={() => setOpenDeleteConfirmModal(false)} />
          <Button
            isDanger
            label='削除'
            onClick={() => {
              onRemove();
            }}
            data-testid='btn_remove'
          />
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default ImageViewerDrawer;
